import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CalendarFilters.module.scss';
import { useToasts } from 'react-toast-notifications';
import { LOGGER_LOG_TYPE, removeAccents } from 'Config';
import Logger from 'common/services/Logger';
import Button from 'common/components/button/Button';
import { Col, Row } from 'react-flexbox-grid';
import FormItem from 'common/components/formItem/FormItem';
import Label from 'common/components/label/Label';
import Select from 'common/components/select/Select';
import SelectController from 'common/components/select/SelectController';
import InputController from 'common/components/input/InputController';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { useForm } from 'react-hook-form';
import Utils from 'common/services/Utils';
import VehicleBrandsService from 'api/vehicleBrands/VehicleBrandsService';
import VehicleModelsService from 'api/vehicleModels/VehicleModelsService';
import VehicleSegmentsService from 'api/vehicleSegments/VehicleSegmentsService';
import LocalsService from 'api/locals/LocalsService';
import DriversService from 'api/drivers/DriversService';
import { CatalogModelsSearchCriteria } from 'api/catalog/models/CatalogModelsSearchCriteria';
import { CatalogBrandsSearchCriteria } from 'api/catalog/models/CatalogBrandsSearchCriteria';

export interface CalendarFiltersModel {
    brandId?: string | null;
    brandName?: string | null;
    modelId?: string | null;
    modelName?: string | null;
    segmentId?: string | null;
    segmentName?: string | null;
    registrationNumber?: string | null;
    localIds?: string[];
    localNames?: string[];
    localOperator?: string;
    driverId?: string | null;
    driverName?: string | null;
}

type Props = {
    filters: CalendarFiltersModel;
    onFilter: (filters: CalendarFiltersModel) => void;
}

const CalendarFilters: React.FC<Props> = ({ filters, onFilter }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const form = useForm<CalendarFiltersModel>({ shouldUnregister: false, shouldFocusError: true, defaultValues: filters });
    const { getValues, setValue, reset, watch } = form;

    const [brandsOptions, setBrandsOptions] = useState<SelectValueLabel[]>([]);
    const [modelsOptions, setModelsOptions] = useState<SelectValueLabel[]>([]);
    const [segmentsOptions, setSegmentsOptions] = useState<SelectValueLabel[]>([]);
    const [localsOptions, setLocalsOptions] = useState<SelectValueLabel[]>([]);
    const [driversOptions, setDriversOptions] = useState<SelectValueLabel[]>([]);

    useEffect(() => {
        form.reset(filters);
    }, [filters]);

    const getData = async () => {
        try {
            const [brands, models, segments, locals, drivers] = await Promise.all([
                VehicleBrandsService.catalog(),
                VehicleModelsService.catalog(),
                VehicleSegmentsService.catalog(),
                LocalsService.catalog(),
                DriversService.catalog(),
            ]);
            setBrandsOptions(brands);
            setModelsOptions(models);
            setSegmentsOptions(segments);
            setLocalsOptions([{ value: '00000000-0000-0000-0000-000000000000', label: t('maintenances.list.none') as string }, ...Utils.sortAlphabetically(locals)]);
            setDriversOptions(drivers);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get filter data`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    };

    useEffect(() => {
        getData().then(() => {
            if (filters.brandId != undefined) {
                setInitialModels();
            }
            if (filters.modelId != undefined) {
                setInitialBrands();
            }
        });
    }, []);

    const setInitialModels = async () => {
        const models = await VehicleModelsService.catalog({ brandId: getValues('brandId') } as CatalogModelsSearchCriteria);
        setModelsOptions(models);
    };

    const setInitialBrands = async () => {
        const brands = await VehicleBrandsService.catalog({ modelId: getValues('modelId') } as CatalogBrandsSearchCriteria);
        setBrandsOptions(brands);
    };

    const getModelsByBrand = async () => {
        try {
            const models = await VehicleModelsService.catalog({ brandId: getValues('brandId') } as CatalogModelsSearchCriteria);
            setModelsOptions(models);
            if (!getValues('modelId') && models.length == 1) {
                setValue('modelId', models.find(x => x.value)?.value);
                setValue('modelName', models.find(x => x.value)?.label);
                getBrandsByModel();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get models`, error);
        }
    };

    const getBrandsByModel = async () => {
        try {
            const brands = await VehicleBrandsService.catalog({ modelId: getValues('modelId') } as CatalogBrandsSearchCriteria);
            setBrandsOptions(brands);
            if (!getValues('brandId') && brands.length == 1) {
                setValue('brandId', brands.find(x => x.value)?.value);
                setValue('brandName', brands.find(x => x.value)?.label);
                getModelsByBrand();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get brands`, error);
        }
    };

    const clearFilters = () => {
        const model: CalendarFiltersModel = {
            brandId: null,
            brandName: null,
            modelId: null,
            modelName: null,
            segmentId: null,
            segmentName: null,
            registrationNumber: null,
            localIds: [],
            localNames: [],
            localOperator: 'OR',
            driverId: null,
            driverName: null,
        };
        reset(model);
        onSubmit(model);
        getData();
    };

    const onSubmit = (f: CalendarFiltersModel) => {
        onFilter(f);
    };

    return (
        <div className={styles.formContent}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <Row>
                    <Col sm={12} md={6} xl={6}>
                        <FormItem>
                            <Label className={styles.filtersLabel}>{t('reservations.filters.brand')}</Label>
                            <SelectController
                                form={form}
                                name="brandId"
                                placeholder={t('reservations.filters.brand')}
                                options={brandsOptions}
                                isDisabled={false}
                                isClearable={true}
                                filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                onChangeSelect={(data: SelectValueLabel) => {
                                    setValue('brandId', data ? data.value : null);
                                    setValue('brandName', data ? data.label : null);
                                    if (!data) {
                                        setValue('modelId', null);
                                        setValue('modelName', null);
                                    }
                                    getModelsByBrand();
                                }}
                            />
                        </FormItem>
                    </Col>
                    <Col sm={12} md={6} xl={6}>
                        <FormItem>
                            <Label className={styles.filtersLabel}>{t('reservations.filters.model')}</Label>
                            <SelectController
                                form={form}
                                name="modelId"
                                placeholder={t('reservations.filters.model')}
                                options={modelsOptions}
                                isDisabled={false}
                                isClearable={true}
                                filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                onChangeSelect={(data: SelectValueLabel) => {
                                    setValue('modelId', data ? data.value : null);
                                    setValue('modelName', data ? data.label : null);
                                    getBrandsByModel();
                                }}
                            />
                        </FormItem>
                    </Col>
                </Row>
                <Row>
                    <Col sm={12} md={6} xl={6}>
                        <FormItem>
                            <Label className={styles.filtersLabel}>{t('reservations.filters.segment')}</Label>
                            <SelectController
                                form={form}
                                name="segmentId"
                                placeholder={t('reservations.filters.segment')}
                                options={segmentsOptions}
                                isDisabled={false}
                                isClearable={true}
                                filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                onChangeSelect={(data: SelectValueLabel) => {
                                    setValue('segmentId', data ? data.value : null);
                                    setValue('segmentName', data ? data.label : null);
                                }}
                            />
                        </FormItem>
                    </Col>
                    <Col sm={12} md={6} xl={6}>
                        <FormItem>
                            <Label className={styles.filtersLabel}>{t('reservations.filters.registration')}</Label>
                            <InputController
                                form={form as any}
                                name="registrationNumber"
                                placeholder={t('reservations.filters.registration')}
                            />
                        </FormItem>
                    </Col>
                </Row>
                <Row>
                    <Col sm={12} md={6} xl={6}>
                        <FormItem>
                            <div className={styles.labelWithOperator}>
                                <Label className={styles.filtersLabel}>{t('reservations.filters.agency')}</Label>
                                <div className={styles.operatorToggle}>
                                    <span title={t('common.filter_all_tooltip' as any) as string}>
                                        <Button
                                            text={t('common.filter_all' as any) as string}
                                            className={styles.firstChild}
                                            preset={watch('localOperator') === 'AND' ? 'primary' : 'secondary'}
                                            onClick={() => { setValue('localOperator', 'AND'); }}
                                            size={'small'}
                                        />
                                    </span>
                                    <span title={t('common.filter_any_tooltip' as any) as string}>
                                        <Button
                                            text={t('common.filter_any' as any) as string}
                                            className={styles.lastChild}
                                            preset={watch('localOperator') !== 'AND' ? 'primary' : 'secondary'}
                                            onClick={() => { setValue('localOperator', 'OR'); }}
                                            size={'small'}
                                        />
                                    </span>
                                </div>
                            </div>
                            <Select
                                options={localsOptions}
                                isMulti
                                isClearable
                                placeholder={t('reservations.filters.agency')}
                                onChange={(data: SelectValueLabel[]) => {
                                    setValue('localIds', data?.map(d => d.value) ?? []);
                                    setValue('localNames', data?.map(d => d.label) ?? []);
                                }}
                                value={localsOptions.filter(x => (watch('localIds') ?? []).includes(x.value))}
                            />
                        </FormItem>
                    </Col>
                    <Col sm={12} md={6} xl={6}>
                        <FormItem>
                            <Label className={styles.filtersLabel}>{t('reservations.filters.driver')}</Label>
                            <SelectController
                                form={form}
                                name="driverId"
                                placeholder={t('reservations.filters.driver')}
                                options={driversOptions}
                                isDisabled={false}
                                isClearable={true}
                                filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                onChangeSelect={(data: SelectValueLabel) => {
                                    setValue('driverId', data ? data.value : null);
                                    setValue('driverName', data ? data.label : null);
                                }}
                            />
                        </FormItem>
                    </Col>
                </Row>
                <Row>
                    <Col xl={12} className={styles.searchButton}>
                        <Button
                            text={t('common.clear')}
                            size={'small'}
                            preset={'secondary'}
                            onClick={clearFilters}
                            type='reset'
                        />
                        <Button
                            type='submit'
                            text={t('common.apply')}
                            size={'small'}
                        />
                    </Col>
                </Row>
            </form>
        </div>
    );
};

export default memo(CalendarFilters);
